// ======= Action Types =======
import {
    FETCH_PRIMARY_TRAINING_DEVICE_START,
    FETCH_PRIMARY_TRAINING_DEVICE_SUCCESS,
    FETCH_PRIMARY_TRAINING_DEVICE_FAIL,
    UPDATE_PRIMARY_TRAINING_DEVICE_START,
    UPDATE_PRIMARY_TRAINING_DEVICE_SUCCESS,
    UPDATE_PRIMARY_TRAINING_DEVICE_FAIL,
    UPDATE_PRIMARY_ACTIVITY_TRACKER_START,
    UPDATE_PRIMARY_ACTIVITY_TRACKER_SUCCESS,
    UPDATE_PRIMARY_ACTIVITY_TRACKER_FAIL,
    REMOVE_DEVICE_SUCCESS
} from '../../actions/ActionTypes.js';

// ======= Initial State =======
const INITIAL_STATE = {
    primaryTrainingDevice: null,
    PrimaryTrainingDevices: {
        deviceWeights: [],
        primaryTrainingDeviceCount: 0
    },
    RegisteredDevices: [],
    TrainingStatusOnlyDevices: {
        deviceWeights: []
    },
    WearableDevices: {
        deviceWeights: [],
        wearableDeviceCount: 0
    },
    fetchingPrimaryTrainingDevice: false,
    fetchPrimaryTrainingDeviceSuccess: false,
    fetchPrimaryTrainingDeviceFail: false,
    updatingPrimaryTrainingDevice: false,
    updatePrimaryTrainingDeviceSuccess: false,
    updatePrimaryTrainingDeviceFail: false,
    updatingPrimaryActivityTracker: false,
    updatePrimaryActivityTrackerSuccess: false,
    updatePrimaryActivityTrackerFail: false
};

export const isFetchingPrimaryTrainingDevice = (state) => state.primaryTrainingDevice.fetchingPrimaryTrainingDevice;
export const isFetchPrimaryTrainingDeviceSuccess = (state) =>
    state.primaryTrainingDevice.fetchPrimaryTrainingDeviceSuccess;
export const isFetchPrimaryTrainingDeviceFail = (state) => state.primaryTrainingDevice.fetchPrimaryTrainingDeviceFail;
export const getPrimaryTrainingDevice = (state) => state.primaryTrainingDevice.PrimaryTrainingDevice;
export const getPrimaryActivityTracker = (state) => {
    const devices = state.primaryTrainingDevice.WearableDevices.deviceWeights;
    if (devices?.length > 0) {
        const primaryActivityTracker = devices.find((device) => device?.primaryWearableDevice);
        return primaryActivityTracker;
    }
};
export const getPrimaryTrainingDeviceDetails = (state) => {
    const devices = state.primaryTrainingDevice.PrimaryTrainingDevices.deviceWeights;
    const primaryTrainingDevice = state.primaryTrainingDevice.PrimaryTrainingDevice;
    if (devices && primaryTrainingDevice) {
        const primaryTrainingDeviceDetails = devices.find((device) => {
            return device.deviceId === primaryTrainingDevice.deviceId;
        });
        return primaryTrainingDeviceDetails;
    }
};
export const getRegisteredDevices = (state) => state.primaryTrainingDevice.RegisteredDevices;
export const getPrimaryTrainingDevices = (state) => state.primaryTrainingDevice.PrimaryTrainingDevices.deviceWeights;
export const getPrimaryWearableDevices = (state) => state.primaryTrainingDevice.WearableDevices.deviceWeights;
export const isUpdatingPrimaryTrainingDevice = (state) => state.primaryTrainingDevice.updatingPrimaryTrainingDevice;
export const isUpdatePrimaryTrainingDeviceSuccess = (state) =>
    state.primaryTrainingDevice.updatePrimaryTrainingDeviceSuccess;
export const isUpdatePrimaryTrainingDeviceFail = (state) => state.primaryTrainingDevice.updatePrimaryTrainingDeviceFail;
export const isUpdatingPrimaryActivityTracker = (state) => state.primaryTrainingDevice.updatingPrimaryActivityTracker;
export const isUpdatePrimaryActivityTrackerSuccess = (state) =>
    state.primaryTrainingDevice.updatePrimaryActivityTrackerSuccess;
export const isUpdatePrimaryActivityTrackerFail = (state) =>
    state.primaryTrainingDevice.updatePrimaryActivityTrackerFail;
export const shouldShowPrimaryTrainingDevice = (state) => {
    return state.primaryTrainingDevice.PrimaryTrainingDevices.primaryTrainingDeviceCount > 1;
};
export const shouldShowPrimaryWearable = (state) => {
    return state.primaryTrainingDevice.WearableDevices.wearableDeviceCount > 1;
};
export const shouldShowDevicePriority = (state) => {
    return shouldShowPrimaryTrainingDevice(state) || shouldShowPrimaryWearable(state);
};
export const getIncompatiblePTDs = (state) => {
    return state.primaryTrainingDevice.TrainingStatusOnlyDevices.deviceWeights;
};

export const hasTrainingPlanCapableDevice = (state) => {
    const devices = state.primaryTrainingDevice.RegisteredDevices;
    if (!devices?.length) return false;

    return devices.some((device) => device.trainingStatusCapable);
};

// ======= Default Action =======
export default function primaryTrainingDevice(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}

// ======= Actions =======
const fetchingPrimaryTrainingDevice = (state) => ({
    ...state,
    fetchingPrimaryTrainingDevice: true
});

const updatingPrimaryTrainingDevice = (state) => ({
    ...state,
    updatingPrimaryTrainingDevice: true
});

const updatingPrimaryActivityTracker = (state) => ({
    ...state,
    updatingPrimaryActivityTracker: true
});

const fetchPrimaryTrainingDeviceFinished = (success) => (state, action) => ({
    ...state,
    ...(success ? action.data : null),
    fetchingPrimaryTrainingDevice: false,
    fetchPrimaryTrainingDeviceSuccess: success,
    fetchPrimaryTrainingDeviceFail: !success
});

const updatePrimaryTrainingDeviceFinished = (success) => (state, action) => ({
    ...state,
    ...(success ? { PrimaryTrainingDevice: { deviceId: action.deviceId } } : null),
    updatingPrimaryTrainingDevice: false,
    updatePrimaryTrainingDeviceSuccess: success,
    updatePrimaryTrainingDeviceFail: !success
});

const updatePrimaryActivityTrackerFinished = (success) => (state, action) => ({
    ...state,
    ...(success
        ? {
              RegisteredDevices: state.RegisteredDevices.map((device) => {
                  if (device.primaryActivityTrackerIndicator) {
                      return { ...device, primaryActivityTrackerIndicator: false };
                  } else if (device.deviceId === action.deviceId) {
                      return { ...device, primaryActivityTrackerIndicator: true };
                  } else {
                      return device;
                  }
              })
          }
        : null),
    ...(success
        ? {
              WearableDevices: {
                  ...state.WearableDevices,
                  deviceWeights: state.WearableDevices.deviceWeights.map((device) => {
                      if (device.primaryWearableDevice) {
                          return { ...device, primaryWearableDevice: false };
                      } else if (device.deviceId === action.deviceId) {
                          return { ...device, primaryWearableDevice: true };
                      } else {
                          return device;
                      }
                  })
              }
          }
        : null),
    updatingPrimaryActivityTracker: false,
    updatePrimaryActivityTrackerSuccess: success,
    updatePrimaryActivityTrackerFail: !success
});

const removeDeviceSuccess = (state, action) => ({
    ...state,
    RegisteredDevices: state.RegisteredDevices.filter((device) => device.deviceId !== action.deviceId),
    WearableDevices: {
        ...state.WearableDevices,
        deviceWeights: state.WearableDevices.deviceWeights.filter((device) => device.deviceId !== action.deviceId),
        wearableDeviceCount: state.WearableDevices.deviceWeights.some((device) => device.deviceId === action.deviceId)
            ? state.WearableDevices.wearableDeviceCount - 1
            : state.WearableDevices.wearableDeviceCount
    },
    PrimaryTrainingDevices: {
        ...state.PrimaryTrainingDevices,
        deviceWeights: state.PrimaryTrainingDevices.deviceWeights.filter(
            (device) => device.deviceId !== action.deviceId
        ),
        primaryTrainingDeviceCount: state.PrimaryTrainingDevices.deviceWeights.some(
            (device) => device.deviceId === action.deviceId
        )
            ? state.PrimaryTrainingDevices.primaryTrainingDeviceCount - 1
            : state.PrimaryTrainingDevices.primaryTrainingDeviceCount
    },
    TrainingStatusOnlyDevices: {
        ...state.TrainingStatusOnlyDevices,
        deviceWeights: state.TrainingStatusOnlyDevices.deviceWeights.filter(
            (device) => device.deviceId !== action.deviceId
        )
    }
});

// ======= Handler Assignments =======
const handlers = {
    [FETCH_PRIMARY_TRAINING_DEVICE_START]: fetchingPrimaryTrainingDevice,
    [FETCH_PRIMARY_TRAINING_DEVICE_SUCCESS]: fetchPrimaryTrainingDeviceFinished(true),
    [FETCH_PRIMARY_TRAINING_DEVICE_FAIL]: fetchPrimaryTrainingDeviceFinished(false),
    [UPDATE_PRIMARY_TRAINING_DEVICE_START]: updatingPrimaryTrainingDevice,
    [UPDATE_PRIMARY_TRAINING_DEVICE_SUCCESS]: updatePrimaryTrainingDeviceFinished(true),
    [UPDATE_PRIMARY_TRAINING_DEVICE_FAIL]: updatePrimaryTrainingDeviceFinished(false),
    [UPDATE_PRIMARY_ACTIVITY_TRACKER_START]: updatingPrimaryActivityTracker,
    [UPDATE_PRIMARY_ACTIVITY_TRACKER_SUCCESS]: updatePrimaryActivityTrackerFinished(true),
    [UPDATE_PRIMARY_ACTIVITY_TRACKER_FAIL]: updatePrimaryActivityTrackerFinished(false),
    [REMOVE_DEVICE_SUCCESS]: removeDeviceSuccess
};
